import { Edit2, Eye, Plus, Save, ShieldCheck, Trash2 } from 'lucide-react';
import { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import ErrorState from '../components/common/ErrorState';
import Modal from '../components/common/Modal';
import PageHeader from '../components/common/PageHeader';
import SkeletonList from '../components/common/SkeletonList';
import { useApps } from '../contexts/AppsContext';
import componentsService from '../services/components';
import rolesService from '../services/roles';
import { splitRoles } from '../utils/roleFlags';
import { resolveDefaultAppId, setSelectedAppId } from '../utils/storage';

const RolesPage = () => {
    const { apps, loading: loadingApps, error: appsError } = useApps();
    const [appId, setAppId] = useState('');
    const [roles, setRoles] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [successMessage, setSuccessMessage] = useState('');
    const [searchParams] = useSearchParams();

    // Modals
    const [showCreateModal, setShowCreateModal] = useState(false);
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [showRoleComponentsModal, setShowRoleComponentsModal] = useState(false);

    // Selection
    const [selectedRole, setSelectedRole] = useState(null);

    // Component Mapping State
    const [roleComponents, setRoleComponents] = useState([]);
    const [allComponents, setAllComponents] = useState([]);
    const [loadingRoleComponents, setLoadingRoleComponents] = useState(false);
    const [mappingError, setMappingError] = useState('');

    // Add/Edit Mapping Form
    const [newMapping, setNewMapping] = useState({ component_name: '', access: '{"access": "readonly"}' });
    const [editingMappingId, setEditingMappingId] = useState(null);
    const [editAccessValue, setEditAccessValue] = useState('');

    const [formData, setFormData] = useState({
        rolename: '',
        role_detail: '{}',
    });

    useEffect(() => {
        if (apps && apps.length > 0) {
            const appIdFromQuery = searchParams.get('app_id');
            const defaultAppId = resolveDefaultAppId(apps, appIdFromQuery);
            setAppId(defaultAppId);
            loadRoles(defaultAppId);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [apps, searchParams]);

    const loadRoles = async (targetAppId) => {
        if (!targetAppId || targetAppId.trim() === '') return;
        setLoading(true);
        setError('');
        try {
            const data = await rolesService.getAll(targetAppId.trim());
            // Flag roles (presentation 'FLAG') are checkboxes on the user form, never roles here.
            setRoles(splitRoles(data).roles);
        } catch (err) {
            setError(err?.response?.data?.message || err?.message || 'Failed to load roles');
        } finally {
            setLoading(false);
        }
    };

    const handleAppChange = (e) => {
        const selectedAppId = e.target.value;
        setAppId(selectedAppId);
        setSelectedAppId(selectedAppId);
        if (selectedAppId) loadRoles(selectedAppId);
    };

    // Role CRUD -------------------------------------------------------------

    const handleCreateRole = async (e) => {
        e.preventDefault();
        if (!appId) return setError('App ID is required');
        setError('');
        try {
            await rolesService.addRole(appId, {
                rolename: formData.rolename,
                role_detail: JSON.parse(formData.role_detail || '{}'),
            });
            setSuccessMessage('Role created successfully!');
            setShowCreateModal(false);
            resetForm();
            loadRoles(appId);
            setTimeout(() => setSuccessMessage(''), 3000);
        } catch (err) {
            setError(err?.response?.data?.message || err?.message || 'Failed to create role');
        }
    };

    const handleUpdateStatus = async () => {
        if (!appId || !selectedRole) return;
        setError('');
        try {
            await rolesService.updateStatus(appId, {
                rolename: selectedRole.role_name,
                is_active: selectedRole.is_active ? 0 : 1,
            });
            setSuccessMessage(`Role ${selectedRole.is_active ? 'disabled' : 'enabled'} successfully!`);
            setShowDeleteModal(false);
            setSelectedRole(null);
            loadRoles(appId);
            setTimeout(() => setSuccessMessage(''), 3000);
        } catch (err) {
            setError(err?.response?.data?.message || err?.message || 'Failed to update status');
        }
    };

    const handleDeleteRole = async () => {
        if (!appId || !selectedRole) return;
        if (!window.confirm(`Are you sure you want to delete role "${selectedRole.role_name}"?`)) return;
        setError('');
        try {
            await rolesService.deleteRole(appId, { rolename: selectedRole.role_name });
            setSuccessMessage('Role deleted successfully!');
            setShowDeleteModal(false);
            setSelectedRole(null);
            loadRoles(appId);
            setTimeout(() => setSuccessMessage(''), 3000);
        } catch (err) {
            setError(err?.response?.data?.message || err?.message || 'Failed to delete role');
        }
    };

    // Mapping CRUD ----------------------------------------------------------

    const openRoleComponents = async (role) => {
        if (!appId) return;
        setSelectedRole(role);
        setRoleComponents([]);
        setAllComponents([]);
        setLoadingRoleComponents(true);
        setShowRoleComponentsModal(true);
        setMappingError('');
        setNewMapping({ component_name: '', access: '{"access": "readonly"}' });
        setEditingMappingId(null);
        try {
            // Load Mappings
            const roleData = await rolesService.getRoleDetail(appId, role.role_name);
            const rawComps =
                Array.isArray(roleData) && roleData[0]?.component_details ? roleData[0].component_details : [];
            setRoleComponents(rawComps.filter((c) => c.component_id)); // Filter out nulls

            // Load Available Components for Dropdown
            const allComps = await componentsService.getAll(appId);
            setAllComponents(allComps || []);
        } catch (err) {
            setMappingError(err?.response?.data?.message || err?.message || 'Failed to load data');
        } finally {
            setLoadingRoleComponents(false);
        }
    };

    const refreshMappings = async () => {
        if (!selectedRole) return;
        try {
            const roleData = await rolesService.getRoleDetail(appId, selectedRole.role_name);
            const rawComps =
                Array.isArray(roleData) && roleData[0]?.component_details ? roleData[0].component_details : [];
            setRoleComponents(rawComps.filter((c) => c.component_id)); // Filter out nulls
        } catch (err) {
            console.error(err);
        }
    };

    const handleAddMapping = async (e) => {
        e.preventDefault();
        if (!newMapping.component_name) return setMappingError('Select a component');
        setMappingError('');
        try {
            await componentsService.addRoleMapping(appId, {
                rolename: selectedRole.role_name,
                component_name: newMapping.component_name,
                role_component_access: JSON.parse(newMapping.access),
            });
            setNewMapping({ ...newMapping, component_name: '' }); // Reset component selection
            await refreshMappings();
        } catch (err) {
            setMappingError(err?.response?.data?.message || err?.message || 'Failed to add mapping');
        }
    };

    const handleDeleteMapping = async (componentName) => {
        if (!window.confirm(`Unmap ${componentName} from ${selectedRole.role_name}?`)) return;
        setMappingError('');
        try {
            await componentsService.removeRoleMapping(appId, {
                rolename: selectedRole.role_name,
                component_name: componentName,
            });
            await refreshMappings();
        } catch (err) {
            setMappingError(err?.response?.data?.message || err?.message || 'Failed to remove mapping');
        }
    };

    const startEditing = (comp) => {
        setEditingMappingId(comp.component_id);
        const accessVal = typeof comp.access === 'string' ? comp.access : JSON.stringify(comp.access || {});
        setEditAccessValue(accessVal);
    };

    const handleUpdateMapping = async (componentName) => {
        setMappingError('');
        try {
            await componentsService.updateRoleAccess(appId, {
                rolename: selectedRole.role_name,
                component_name: componentName,
                updated_access: JSON.parse(editAccessValue),
            });
            setEditingMappingId(null);
            await refreshMappings();
        } catch (err) {
            setMappingError(err?.response?.data?.message || err?.message || 'Failed to update access');
        }
    };

    const resetForm = () => {
        setFormData({ rolename: '', role_detail: '{}' });
    };

    const openStatusModal = (role) => {
        setSelectedRole(role);
        setShowDeleteModal(true);
    };

    // Helper to filter out already mapped components
    const availableComponents = allComponents.filter(
        (c) => !roleComponents.some((rc) => rc.component_name === c.component_name)
    );

    return (
        <div className="space-y-4">
            <PageHeader
                title="Roles"
                subtitle={`Manage roles for ${appId ? `App: ${appId.substring(0, 8)}...` : 'selected app'}`}
                actions={
                    <div className="flex gap-2">
                        <select
                            className="rounded-lg border border-slate-200 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-200 min-w-[200px]"
                            value={appId}
                            onChange={handleAppChange}
                            disabled={loadingApps}
                        >
                            <option value="">Select an App</option>
                            {apps.map((app) => (
                                <option key={app.app_id} value={app.app_id}>
                                    {app.app_name} {app.is_active ? '' : '(Disabled)'}
                                </option>
                            ))}
                        </select>
                        {appId && (
                            <button
                                onClick={() => {
                                    resetForm();
                                    setShowCreateModal(true);
                                }}
                                className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-primary-600 text-white text-sm font-medium hover:bg-primary-700"
                            >
                                <Plus className="h-4 w-4" /> Add Role
                            </button>
                        )}
                    </div>
                }
            />

            {successMessage && (
                <div className="rounded-lg bg-emerald-50 border border-emerald-200 text-emerald-700 px-4 py-3 text-sm">
                    {successMessage}
                </div>
            )}

            {loadingApps ? (
                <SkeletonList />
            ) : appsError ? (
                <ErrorState message={appsError} />
            ) : loading ? (
                <SkeletonList />
            ) : error ? (
                <ErrorState message={error} />
            ) : !appId ? (
                <div className="rounded-xl border border-slate-200 bg-white/90 backdrop-blur p-8 text-center">
                    <p className="text-slate-500">Select an App to view roles.</p>
                </div>
            ) : roles.length === 0 ? (
                <div className="rounded-xl border border-slate-200 bg-white/90 backdrop-blur p-8 text-center">
                    <p className="text-slate-500">No roles found for the selected app.</p>
                </div>
            ) : (
                <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-3">
                    {roles.map((role) => (
                        <div
                            key={role.role_id}
                            className="rounded-2xl border border-slate-200 bg-white/90 backdrop-blur p-4 shadow-sm hover:shadow-md transition space-y-2"
                        >
                            <div className="flex items-center justify-between">
                                <div className="flex items-center gap-2">
                                    <div className="h-9 w-9 rounded-lg bg-primary-50 text-primary-700 grid place-content-center shadow-inner">
                                        <ShieldCheck className="h-4 w-4" />
                                    </div>
                                    <div className="text-base font-semibold text-slate-900">{role.role_name}</div>
                                </div>
                                <span
                                    className={`text-xs font-semibold px-2 py-1 rounded-full border ${
                                        role.is_active
                                            ? 'text-emerald-700 bg-emerald-50 border-emerald-100'
                                            : 'text-amber-700 bg-amber-50 border-amber-100'
                                    }`}
                                >
                                    {role.is_active ? 'Active' : 'Disabled'}
                                </span>
                            </div>
                            <div className="text-sm text-slate-600 break-all">{role.role_detail || '—'}</div>
                            <div className="flex gap-2 pt-2 border-t border-slate-100">
                                <button
                                    onClick={() => openRoleComponents(role)}
                                    className="flex-1 px-2 py-1 rounded-md text-xs font-medium text-blue-700 bg-blue-50 hover:bg-blue-100 flex items-center justify-center gap-1"
                                >
                                    <Eye className="h-3 w-3" /> Components
                                </button>
                                <button
                                    onClick={() => openStatusModal(role)}
                                    className={`flex-1 px-2 py-1 rounded-md text-xs font-medium ${
                                        role.is_active
                                            ? 'text-amber-700 bg-amber-50 hover:bg-amber-100'
                                            : 'text-emerald-700 bg-emerald-50 hover:bg-emerald-100'
                                    }`}
                                >
                                    {role.is_active ? 'Disable' : 'Enable'}
                                </button>
                                <button
                                    onClick={() => {
                                        setSelectedRole(role);
                                        setShowDeleteModal(true);
                                    }}
                                    className="flex-1 px-2 py-1 rounded-md text-xs font-medium text-red-700 bg-red-50 hover:bg-red-100 flex items-center justify-center gap-1"
                                >
                                    <Trash2 className="h-3 w-3" /> Delete
                                </button>
                            </div>
                        </div>
                    ))}
                </div>
            )}

            {/* Create Role Modal */}
            <Modal isOpen={showCreateModal} onClose={() => setShowCreateModal(false)} title="Create New Role" size="md">
                <form onSubmit={handleCreateRole} className="space-y-4">
                    <div>
                        <label className="block text-sm font-medium text-slate-700 mb-1">Role Name *</label>
                        <input
                            type="text"
                            required
                            value={formData.rolename}
                            onChange={(e) => setFormData({ ...formData, rolename: e.target.value })}
                            className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-200"
                            placeholder="Role Name"
                        />
                    </div>
                    <div>
                        <label className="block text-sm font-medium text-slate-700 mb-1">Role Detail (JSON) *</label>
                        <textarea
                            required
                            rows={4}
                            value={formData.role_detail}
                            onChange={(e) => setFormData({ ...formData, role_detail: e.target.value })}
                            className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm font-mono focus:outline-none focus:ring-2 focus:ring-primary-200"
                            placeholder='{"access": "read"}'
                        />
                    </div>

                    {error && (
                        <div className="text-sm text-red-600 bg-red-50 border border-red-100 rounded-lg px-3 py-2">
                            {error}
                        </div>
                    )}

                    <div className="flex gap-3 pt-4 border-t border-slate-200">
                        <button
                            type="submit"
                            className="flex-1 rounded-lg bg-primary-600 text-white font-medium py-2.5 hover:bg-primary-700"
                        >
                            Create Role
                        </button>
                        <button
                            type="button"
                            onClick={() => setShowCreateModal(false)}
                            className="flex-1 rounded-lg border border-slate-200 text-slate-700 font-medium py-2.5 hover:bg-slate-50"
                        >
                            Cancel
                        </button>
                    </div>
                </form>
            </Modal>

            {/* Status/Delete Modal */}
            <Modal
                isOpen={showDeleteModal}
                onClose={() => {
                    setShowDeleteModal(false);
                    setSelectedRole(null);
                }}
                title={selectedRole ? `Manage ${selectedRole.role_name}` : 'Manage Role'}
                size="sm"
            >
                <div className="space-y-4">
                    {selectedRole && (
                        <div className="space-y-3">
                            <div>
                                <p className="text-sm text-slate-600">Current Status:</p>
                                <p
                                    className={`text-sm font-medium ${
                                        selectedRole.is_active ? 'text-emerald-700' : 'text-amber-700'
                                    }`}
                                >
                                    {selectedRole.is_active ? 'Active' : 'Disabled'}
                                </p>
                            </div>
                            <div className="flex gap-3">
                                <button
                                    onClick={handleUpdateStatus}
                                    className={`flex-1 rounded-lg font-medium py-2.5 ${
                                        selectedRole.is_active
                                            ? 'bg-amber-50 text-amber-700 hover:bg-amber-100 border border-amber-200'
                                            : 'bg-emerald-50 text-emerald-700 hover:bg-emerald-100 border border-emerald-200'
                                    }`}
                                >
                                    {selectedRole.is_active ? 'Disable Role' : 'Enable Role'}
                                </button>
                                <button
                                    onClick={handleDeleteRole}
                                    className="flex-1 rounded-lg bg-red-50 text-red-700 font-medium py-2.5 hover:bg-red-100 border border-red-200"
                                >
                                    Delete Role
                                </button>
                            </div>
                        </div>
                    )}

                    {error && (
                        <div className="text-sm text-red-600 bg-red-50 border border-red-100 rounded-lg px-3 py-2">
                            {error}
                        </div>
                    )}

                    <div className="pt-4 border-t border-slate-200">
                        <button
                            onClick={() => {
                                setShowDeleteModal(false);
                                setSelectedRole(null);
                            }}
                            className="w-full rounded-lg border border-slate-200 text-slate-700 font-medium py-2.5 hover:bg-slate-50"
                        >
                            Close
                        </button>
                    </div>
                </div>
            </Modal>

            {/* Role Components Modal (Mappings) */}
            <Modal
                isOpen={showRoleComponentsModal}
                onClose={() => {
                    setShowRoleComponentsModal(false);
                    setSelectedRole(null);
                    setRoleComponents([]);
                    setMappingError('');
                }}
                title={selectedRole ? `Manage Access for ${selectedRole.role_name}` : 'Component Access'}
                size="lg"
            >
                <div className="space-y-6">
                    {/* Add New Mapping Form */}
                    <form
                        onSubmit={handleAddMapping}
                        className="p-4 bg-slate-50 rounded-xl border border-slate-100 space-y-3"
                    >
                        <h4 className="text-sm font-semibold text-slate-800">Assign New Component</h4>
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                            <select
                                className="rounded-lg border border-slate-200 px-3 py-2 text-sm"
                                value={newMapping.component_name}
                                onChange={(e) => setNewMapping({ ...newMapping, component_name: e.target.value })}
                                required
                            >
                                <option value="">Select Component...</option>
                                {availableComponents.map((c) => (
                                    <option key={c.component_id} value={c.component_name}>
                                        {c.component_name}
                                    </option>
                                ))}
                            </select>
                            <input
                                className="rounded-lg border border-slate-200 px-3 py-2 text-sm font-mono"
                                value={newMapping.access}
                                onChange={(e) => setNewMapping({ ...newMapping, access: e.target.value })}
                                placeholder="Access JSON"
                                required
                            />
                            <button
                                type="submit"
                                className="rounded-lg bg-primary-600 text-white px-4 py-2 text-sm font-medium hover:bg-primary-700"
                            >
                                Assign Access
                            </button>
                        </div>
                    </form>

                    {/* Error Message */}
                    {mappingError && (
                        <div className="text-sm text-red-600 bg-red-50 border border-red-100 rounded-lg px-3 py-2">
                            {mappingError}
                        </div>
                    )}

                    {/* Components Table */}
                    {loadingRoleComponents ? (
                        <SkeletonList />
                    ) : roleComponents.length === 0 ? (
                        <div className="text-center py-8 text-slate-500 bg-slate-50 rounded-xl border border-slate-100 border-dashed">
                            No components assigned to this role yet.
                        </div>
                    ) : (
                        <div className="overflow-x-auto rounded-xl border border-slate-200">
                            <table className="min-w-full text-sm text-left text-slate-700">
                                <thead className="bg-slate-50 text-xs uppercase text-slate-500 font-semibold">
                                    <tr>
                                        <th className="px-4 py-3">Component</th>
                                        <th className="px-4 py-3">Access Config</th>
                                        <th className="px-4 py-3 text-right">Actions</th>
                                    </tr>
                                </thead>
                                <tbody className="divide-y divide-slate-100">
                                    {roleComponents.map((comp) => (
                                        <tr key={comp.component_id} className="hover:bg-slate-50/50">
                                            <td className="px-4 py-3 align-top">
                                                <div className="font-semibold text-slate-900">
                                                    {comp.component_name}
                                                </div>
                                                <div className="text-xs text-slate-500 truncate max-w-[200px]">
                                                    {comp.component_description || '—'}
                                                </div>
                                            </td>
                                            <td className="px-4 py-3 align-top">
                                                {editingMappingId === comp.component_id ? (
                                                    <div className="space-y-2">
                                                        <textarea
                                                            className="w-full rounded-md border border-slate-300 px-2 py-1 text-xs font-mono"
                                                            rows={2}
                                                            value={editAccessValue}
                                                            onChange={(e) => setEditAccessValue(e.target.value)}
                                                        />
                                                        <div className="flex gap-2">
                                                            <button
                                                                onClick={() => handleUpdateMapping(comp.component_name)}
                                                                className="text-xs bg-emerald-100 text-emerald-700 px-2 py-1 rounded hover:bg-emerald-200 flex items-center gap-1"
                                                            >
                                                                <Save size={12} /> Save
                                                            </button>
                                                            <button
                                                                onClick={() => setEditingMappingId(null)}
                                                                className="text-xs bg-slate-100 text-slate-700 px-2 py-1 rounded hover:bg-slate-200"
                                                            >
                                                                Cancel
                                                            </button>
                                                        </div>
                                                    </div>
                                                ) : (
                                                    <code className="text-xs bg-slate-100 px-2 py-1 rounded border border-slate-200 block text-slate-600 font-mono break-all max-w-[300px]">
                                                        {typeof comp.access === 'string'
                                                            ? comp.access
                                                            : JSON.stringify(comp.access || {})}
                                                    </code>
                                                )}
                                            </td>
                                            <td className="px-4 py-3 text-right align-top">
                                                <div className="flex justify-end gap-2">
                                                    {editingMappingId !== comp.component_id && (
                                                        <>
                                                            <button
                                                                onClick={() => startEditing(comp)}
                                                                className="p-1.5 text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                                                                title="Edit Access"
                                                            >
                                                                <Edit2 size={16} />
                                                            </button>
                                                            <button
                                                                onClick={() => handleDeleteMapping(comp.component_name)}
                                                                className="p-1.5 text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                                                                title="Remove Mapping"
                                                            >
                                                                <Trash2 size={16} />
                                                            </button>
                                                        </>
                                                    )}
                                                </div>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    )}
                </div>
            </Modal>
        </div>
    );
};

export default RolesPage;
